import React from 'react'
import { formatPrice } from 'helpers'
import { AcademyCourseData } from 'modules/academy'
import { useCartContext } from 'modules/cart'
import { useInquire } from 'modules/payment'
import { useSubscription } from 'modules/subscription'

import { Text } from 'components/dataDisplay'

import messages from './messages'


type AcademyCoursePriceProps = {
  price: string // current price in cart
  prices: AcademyCourseData['product']['tradingItems']['items'][number]['prices']
  isCart: boolean
}

const AcademyCoursePrice: React.FC<AcademyCoursePriceProps> = (props) => {
  const { price, prices, isCart } = props

  // ATTN we don't load the cart, we use it context. So it will be null on the payment and thank you pages
  const cartContext = useCartContext()
  const inquireContext = useInquire()
  // we either in cart or payment page
  const hasSubscriptionInOrder = cartContext?.cart?.hasSubscription || inquireContext?.orderData?.hasSubscription

  const { subscription } = useSubscription()

  const regularPrice = formatPrice(prices.standardPrice.amountCents)
  const membershipPrice = formatPrice(prices.membershipPrice.amountCents)

  const MembershipPrice = ({ className, dataTestId }: { className?: string, dataTestId?: string }) => (
    <>
      <Text className={className} style="p6" color="gold-70" message={messages.memberPrice} />
      <Text style="h8" color="gold-70" data-testid={dataTestId}>{membershipPrice}</Text>
    </>
  )

  const RegularPrice = ({ className, strikeThrough, dataTestId }: { className?: string, strikeThrough?: boolean, dataTestId?: string }) => (
    <>
      <Text className={className} style="p6" message={messages.regular} />
      <Text style="h8" tag={strikeThrough ? 's' : 'div'} data-testid={dataTestId}>{regularPrice}</Text>
    </>
  )

  // in cart
  if (isCart) {
    // Case 1. Use with active subscription
    if (subscription.isActive) {
      return (
        <>
          <MembershipPrice dataTestId="price" />
          <RegularPrice className="mt-8" strikeThrough dataTestId="oldPrice" />
        </>
      )
    }
    // Case 2. User has subscription in the cart and membership discount is applied
    else if (hasSubscriptionInOrder) {
      return (
        <>
          <RegularPrice strikeThrough dataTestId="oldPrice" />
          <MembershipPrice className="mt-8" dataTestId="price" />
        </>
      )
    }
    // Case 3. User has no subscription in the cart, no discount is applied
    else {
      return (
        <>
          <RegularPrice dataTestId="price" />
          <MembershipPrice className="mt-8" dataTestId="membershipPrice" />
        </>
      )
    }
  }
  // payment page
  else if (inquireContext) {
    if (subscription.isActive || hasSubscriptionInOrder) {
      return (
        <>
          <MembershipPrice dataTestId="price" />
          <RegularPrice className="mt-8" strikeThrough dataTestId="oldPrice" />
        </>
      )
    }
  }
  // thank-you page
  else {
    // Case 1. Member in thankyou page
    if (subscription.isActive) {
      return (
        <>
          <MembershipPrice dataTestId="price" />
        </>
      )
    }
  }

  return (
    <Text style="t2" message={price} data-testid="price" />
  )
}

export default AcademyCoursePrice
